import { inject } from '@angular/core';
import { type CanActivateFn } from '@angular/router';
import { StatusIndicatorService } from '@complexcare/harmony';
import { RemoteAuthFacadeService } from '../services/remote-auth-facade.service';
import { OfflineAuthService } from '../../../offline-auth/offline-auth.service';
import { checkNetworkOnline } from '../../../offline-auth/network-connectivity.util';
import { LOOKUP_API_BASE_URL } from '../../../http/api-client.tokens';

/**
 * Route guard for remote MFEs. Redirects unauthenticated users to the shell login without initiating a local login
 * flow. Apply to all protected routes in `entry.routes.ts`.
 *
 * When offline or offline-authenticated, the guard allows passthrough so local offline functionality works.
 *
 * The guard uses `window.location.pathname` as the `returnPath` hint so the shell can navigate back to the correct
 * remote route after successful authentication.
 */
export const remoteAuthGuard: CanActivateFn = async (_route, state) => {
  const authFacade = inject(RemoteAuthFacadeService);
  const offlineAuth = inject(OfflineAuthService, { optional: true });
  const lookupApiUrl = inject(LOOKUP_API_BASE_URL, { optional: true });
  const statusIndicator = inject(StatusIndicatorService, { optional: true });

  // When offline (verified via health probe) or authenticated offline via PIN, allow navigation
  const isOnline = await checkNetworkOnline(lookupApiUrl);
  // Only confirmed-online results are synced here; offline transitions belong to the monitoring probe.
  if (isOnline) statusIndicator?.reportProbeResult(true);
  const isOfflineAuth = offlineAuth?.offlineAuthenticated();

  if (!isOnline || isOfflineAuth) {
    return true;
  }

  if (authFacade.isAuthenticated()) {
    return true;
  }

  authFacade.redirectToShellIfUnauthenticated(window.location.pathname);
  return false;
};
